import { animate, MotionValue, useMotionValue, useMotionValueEvent } from 'framer-motion';

/**
 * Use this to create fade-out effects on scrollable containers.
 * Returned mask must be applied to motion element e.g motion.div.
 *
 * @example
 * ```tsx
 * const contentRef = useRef<HTMLDivElement>(null);
 * const { scrollYProgress } = useScroll({ container: contentRef });
 * const maskImage = useScrollOverflowMask(scrollYProgress);
 *
 * return (
 *   <motion.div className="overflow-auto" style={{ maskImage }}>
 *     ...
 *   </motion.div>
 * )
 * ```
 */
export function useScrollOverflowMask(
  scrollProgress: MotionValue<number | null>,
  {
    direction = 'vertical',
  }: {
    direction?: 'vertical' | 'horizontal';
  } = {},
) {
  const deg = direction === 'vertical' ? '180deg' : '90deg';
  const start = `0%`;
  const end = `100%`;
  const startInset = `20%`;
  const endInset = `80%`;
  const transparent = `#0000`;
  const opaque = `#000`;

  const maskImage = useMotionValue(
    `linear-gradient(${deg}, ${opaque}, ${opaque} ${start}, ${opaque} ${endInset}, ${transparent})`,
  );

  useMotionValueEvent(scrollProgress, 'change', (value) => {
    if (value === null) {
      animate(maskImage, `linear-gradient(${deg}, ${opaque}, ${opaque} ${start}, ${opaque} ${end}, ${opaque})`);
    } else if (value === 0) {
      animate(
        maskImage,
        `linear-gradient(${deg}, ${opaque}, ${opaque} ${start}, ${opaque} ${endInset}, ${transparent})`,
      );
    } else if (value === 1) {
      animate(
        maskImage,
        `linear-gradient(${deg}, ${transparent}, ${opaque} ${startInset}, ${opaque} ${end}, ${opaque})`,
      );
    } else if (scrollProgress.getPrevious() === 0 || scrollProgress.getPrevious() === 1) {
      animate(
        maskImage,
        `linear-gradient(${deg}, ${transparent}, ${opaque} ${startInset}, ${opaque} ${endInset}, ${transparent})`,
      );
    }
  });

  return maskImage;
}
